/**
 * Reusable Search Input UI Component
 */

import type { InputHTMLAttributes } from "react";
import { useEffect, useState } from "react";

export interface SearchInputProps
	extends Omit<InputHTMLAttributes<HTMLInputElement>, "type" | "onChange"> {
	readonly value: string;
	readonly onChange: (value: string) => void;
	readonly placeholder?: string;
	readonly disabled?: boolean;
	readonly debounceMs?: number;
}

const SearchIcon = (): React.JSX.Element => (
	<svg
		className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 pointer-events-none peer-focus:text-[#001a41]"
		xmlns="http://www.w3.org/2000/svg"
		fill="none"
		viewBox="0 0 24 24"
		stroke="currentColor"
		strokeWidth={2}
	>
		<path
			strokeLinecap="round"
			strokeLinejoin="round"
			d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
		/>
	</svg>
);

export const SearchInput = ({
	value,
	onChange,
	placeholder = "Search...",
	disabled = false,
	debounceMs = 300,
	className,
	...restProps
}: SearchInputProps): React.JSX.Element => {
	const [internalValue, setInternalValue] = useState(value);

	useEffect(() => {
		setInternalValue(value);
	}, [value]);

	useEffect(() => {
		if (internalValue === value) {
			return;
		}

		const timeoutId = window.setTimeout(() => {
			onChange(internalValue);
		}, debounceMs);

		return () => {
			window.clearTimeout(timeoutId);
		};
	}, [debounceMs, internalValue, onChange, value]);

	const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		setInternalValue(event.target.value);
	};

	return (
		<div className="relative w-full">
			<input
				type="search"
				className={`peer w-full py-2 pl-10 pr-3 rounded-lg border border-gray-200 text-sm leading-6 text-gray-800 bg-white transition-colors duration-150 placeholder:text-gray-400 focus:outline-none focus:border-[#001a41] disabled:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-60 ${className || ""}`}
				value={internalValue}
				onChange={handleChange}
				placeholder={placeholder}
				disabled={disabled}
				{...restProps}
			/>
			<SearchIcon />
		</div>
	);
};

